import { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { User } from '@supabase/supabase-js';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useOrganization } from './OrganizationContext';
import { normalizeDomainInput } from '@/lib/domainNormalization';
import {
  CLAIMANT_ORGANIZATION_ID_COLUMN,
  ClaimantOrganizationBinding,
  readClaimantOrganizationId,
  readWorkspaceDomain,
  readWorkspaceVendorProfileId,
  withClaimantOrganizationId,
} from '@/lib/orgRelationships';
import { getVendorContextBootstrapDelayMs, shouldHydrateVendorContext } from '@/lib/vendorContextRoutes';
import { createBootstrapDeduper } from '@/lib/bootstrapDeduper';
import { isNavigationAbortedFetch } from '@/lib/pageLifecycle';
import {
  getActiveMarketplaceListingsForDomain,
  getVendorProfilesForDomain,
  isSupplierDomainAllowedForListing,
} from '@/lib/supplierDomainClaims';
import {
  acceptVendorTeamInvitation,
  fetchPendingVendorInvitations,
  type PendingVendorInvitation,
} from '@/lib/vendorTeamInvitations';

interface VendorProfile extends ClaimantOrganizationBinding {
  id: string;
  marketplace_listing_id: string;
  hide_marketplace_scores?: boolean;
  domain: string;
  claimed_at: string | null;
  claimed_by: string | null;
  status: 'unclaimed' | 'claimed' | 'verified';
  // ENT-6257: who maintains the profile (vendor_profiles.stewardship / profile_provenance).
  stewardship?: 'vendor' | 'supply_guard' | null;
  profile_provenance?: 'supplier_attested' | 'supply_guard_managed' | 'public_sources' | null;
  created_at: string;
  updated_at: string;
  listing?: {
    company_name: string;
    logo_url: string | null;
    logo_source?: string | null;
    website_url: string | null;
    service_description: string | null;
    supplier_type: string | null;
    country: string | null;
    confirmedCountryCode: string | null;
    status?: string | null;
    domain?: string | null;
    discovery_data?: unknown;
  };
}

interface VendorTeamMember {
  id: string;
  vendor_profile_id: string;
  user_id: string;
  role: 'owner' | 'admin' | 'member';
  invited_at: string;
  accepted_at: string | null;
  is_visible_to_customers?: boolean;
  profile?: {
    email: string;
    first_name: string | null;
    last_name: string | null;
    avatar_url: string | null;
  };
}

interface VendorMembershipRecord extends VendorTeamMember {
  vendor_profile?: VendorProfile;
}

type MarketplaceListingRecord = Omit<NonNullable<VendorProfile['listing']>, 'logo_url'> & {
  id: string;
  domain?: string | null;
  confirmed_country_code?: string | null;
  display_logo_url: string | null;
};

type VendorProfileRecord = ClaimantOrganizationBinding & {
  id: string;
  marketplace_listing_id: string;
  hide_marketplace_scores?: boolean | null;
  claimed_at: string | null;
  claimed_by: string | null;
  status: 'unclaimed' | 'claimed' | 'verified';
  // ENT-6257: canonical maintainer columns (select('*') returns them; the projection keeps them).
  stewardship?: 'vendor' | 'supply_guard' | null;
  profile_provenance?: 'supplier_attested' | 'supply_guard_managed' | 'public_sources' | null;
  created_at: string;
  updated_at: string;
};

type VendorBootstrapState = {
  vendorProfile: VendorProfile | null;
  claimableProfiles: VendorProfile[];
  teamMembers: VendorTeamMember[];
  userRole: 'owner' | 'admin' | 'member' | null;
};

type ContextVendorBootstrapState = {
  contextVendorProfile: VendorProfile | null;
  contextClaimableProfiles: VendorProfile[];
  contextDomain: string | null;
  contextTeamMembers: VendorTeamMember[];
  contextUserRole: 'owner' | 'admin' | 'member' | null;
};

type CompletedClaimWorkspace = {
  organizationId: string;
  vendorProfileId: string;
};

interface VendorContextType {
  vendorProfile: VendorProfile | null;
  /** Explicit advisory deep-link resource, never an authorisation grant. */
  requestedAdvisoryProfileId?: string | null;
  claimableProfiles: VendorProfile[];
  teamMembers: VendorTeamMember[];
  userRole: 'owner' | 'admin' | 'member' | null;
  loading: boolean;
  error: string | null;
  claimProfile: (
    profileId: string,
    overrideDomain?: string
  ) => Promise<{ error: string | null; claimedProfileId?: string }>;
  refreshVendorData: () => Promise<void>;
  refetch: () => Promise<void>;
  canManageProfile: boolean;
  isVendorMode: boolean;
  // ENT-3797: unaccepted invitations for the current user (auth.uid()). These are
  // deliberately NOT treated as active memberships — they grant no access until
  // accepted via acceptInvitation, which drives the accept_vendor_team_invitation
  // RPC.
  pendingInvitations: PendingVendorInvitation[];
  // True when the most recent pending-invitation load failed (RLS/network) rather
  // than genuinely finding none — lets in-shell surfaces offer a retry instead of
  // silently rendering nothing.
  pendingInvitationsError: boolean;
  refreshPendingInvitations: () => Promise<void>;
  // ENT-4086 R16-B: `accessToken` is the ASSERTED session's token (from
  // useInviteAcceptance.assertVerifiedSession()). When supplied it binds the
  // accept RPC to THAT identity (Authorization bearer) so a cross-tab account
  // switch after the identity assert cannot accept as the wrong user. In-app
  // callers (the banner) omit it and keep the global-session behaviour.
  acceptInvitation: (vendorProfileId: string, accessToken?: string) => Promise<{ error: string | null }>;
  // MSP context-aware profile data
  contextVendorProfile: VendorProfile | null;
  contextClaimableProfiles: VendorProfile[];
  contextDomain: string | null;
  contextLoading: boolean;
}

const VendorContext = createContext<VendorContextType | undefined>(undefined);
const vendorBootstrapDeduper = createBootstrapDeduper<VendorBootstrapState>();
const contextVendorBootstrapDeduper = createBootstrapDeduper<ContextVendorBootstrapState>();
const MARKETPLACE_LISTING_SELECT = `
  id,
  company_name,
  display_logo_url:ent5657_display_logo_url,
  logo_source,
  website_url,
  service_description,
  supplier_type,
  country,
  confirmed_country_code,
  status,
  domain
`;

async function loadVendorListingMap(listingIds: string[]) {
  const uniqueListingIds = [...new Set(listingIds.filter(Boolean))];
  if (uniqueListingIds.length === 0) {
    return new Map<string, VendorProfile['listing']>();
  }

  const { data, error } = await supabase
    .from('marketplace_listings')
    .select(MARKETPLACE_LISTING_SELECT)
    .in('id', uniqueListingIds);

  if (error) {
    if (!isNavigationAbortedFetch(error)) console.error('Error fetching vendor listings:', error);
    return new Map<string, VendorProfile['listing']>();
  }

  return new Map(
    ((data ?? []) as MarketplaceListingRecord[]).map((listing) => [
      listing.id,
      {
        company_name: listing.company_name,
        logo_url: listing.display_logo_url,
        logo_source: listing.logo_source ?? null,
        website_url: listing.website_url,
        service_description: listing.service_description,
        supplier_type: listing.supplier_type,
        country: listing.country,
        confirmedCountryCode: listing.confirmed_country_code ?? null,
        status: listing.status ?? null,
        domain: listing.domain ?? null,
      },
    ]),
  );
}

function resolveVendorProfileDomain(
  listing?: VendorProfile['listing'],
) {
  return normalizeDomainInput(listing?.domain) || '';
}

function toVendorProfile(
  rawProfile: VendorProfileRecord,
  listingMap: Map<string, VendorProfile['listing']>,
): VendorProfile {
  const listing = listingMap.get(rawProfile.marketplace_listing_id);
  return withClaimantOrganizationId({
    id: rawProfile.id,
    marketplace_listing_id: rawProfile.marketplace_listing_id,
    hide_marketplace_scores: rawProfile.hide_marketplace_scores ?? false,
    domain: resolveVendorProfileDomain(listing),
    claimed_at: rawProfile.claimed_at,
    claimed_by: rawProfile.claimed_by,
    status: rawProfile.status,
    // ENT-6257 (Codex r4419691009): pass the canonical stewardship fields through, so the
    // operator's managed-profile banner on /vendor/profile sees them.
    stewardship: rawProfile.stewardship ?? null,
    profile_provenance: rawProfile.profile_provenance ?? null,
    created_at: rawProfile.created_at,
    updated_at: rawProfile.updated_at,
    listing,
  }, readClaimantOrganizationId(rawProfile));
}

function attachListingsToProfiles<T extends { marketplace_listing_id: string; domain?: string | null; listing?: VendorProfile['listing'] }>(
  profiles: T[],
  listingMap: Map<string, VendorProfile['listing']>,
): Array<T & { domain: string }> {
  return profiles.map((profile) => ({
    ...profile,
    listing: listingMap.get(profile.marketplace_listing_id) ?? profile.listing,
    domain: resolveVendorProfileDomain(
      listingMap.get(profile.marketplace_listing_id) ?? profile.listing,
    ),
  }));
}

function mapVendorClaimRpcError(message: string | undefined): string {
  switch (message) {
    case 'not_authenticated':
      return 'Not authenticated';
    case 'invalid_claiming_domain':
      return 'Invalid domain for claiming';
    case 'profile_not_found':
      return 'Profile not found';
    case 'claim_forbidden':
      return 'Failed to claim profile. You may not have permission.';
    case 'already_claimed':
      return 'This profile has already been claimed. Please request to join the team.';
    case 'managed_claim_requires_approval':
      return 'This profile is maintained by Supply Guard. Request to claim it from the Claim Supplier Profile page.';
    default:
      return 'Failed to claim profile. Please try again.';
  }
}

// Explicit advisory/action profiles are resolved independently of the active
// shell, but the claimant fallback depends on the accessible organisation set:
// MSP-managed customers hydrate after the first organisation snapshot, so a key
// without them would replay a cached "no profile" result (ENT-6063).
function collectAccessibleOrganizationIds(
  items: ReadonlyArray<{ id?: string | null } | null | undefined>,
): string[] {
  return [...new Set(items.map((item) => item?.id).filter((id): id is string => Boolean(id)))].sort();
}

function buildRequestedVendorBootstrapKey(
  userId: string,
  urlDomain: string | null | undefined,
  requestedProfileId: string,
  accessibleOrganizationIds: readonly string[],
): string {
  return `${userId}:${urlDomain ?? ''}:${requestedProfileId}:${accessibleOrganizationIds.join(',')}`;
}

async function loadVendorMembershipState(
  profileId: string,
  userId: string,
): Promise<Pick<VendorBootstrapState, 'teamMembers' | 'userRole'>> {
  const { data: teamData, error: teamError } = await supabase
    .from('vendor_team_members')
    .select(`*`)
    .eq('vendor_profile_id', profileId);

  if (teamError) throw teamError;

  const teamMembers = (teamData ?? []) as VendorTeamMember[];
  if (teamMembers.length === 0) {
    return {
      teamMembers: [],
      userRole: null,
    };
  }

  const userIds = teamMembers.map((member) => member.user_id);
  const { data: profilesData, error: profilesError } = await supabase
    .from('profiles')
    .select('user_id, email, first_name, last_name, avatar_url')
    .in('user_id', userIds);

  if (profilesError) throw profilesError;

  const profilesMap = new Map(profilesData?.map((profile) => [profile.user_id, profile]) ?? []);
  const teamMembersWithProfiles = teamMembers.map((member) => ({
    ...member,
    profile: profilesMap.get(member.user_id) || undefined,
  })) as VendorTeamMember[];
  const currentMembership = teamMembersWithProfiles.find((member) => member.user_id === userId);

  return {
    teamMembers: teamMembersWithProfiles,
    userRole: currentMembership?.accepted_at ? currentMembership.role : null,
  };
}

export function VendorProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const {
    user,
    organization,
    organizations = [],
    mspCustomerOrgs = [],
    isLoading: authLoading,
    refetch: refetchOrganizations,
  } = useOrganization();
  const [vendorProfile, setVendorProfile] = useState<VendorProfile | null>(null);
  const [claimableProfiles, setClaimableProfiles] = useState<VendorProfile[]>([]);
  const [teamMembers, setTeamMembers] = useState<VendorTeamMember[]>([]);
  const [userRole, setUserRole] = useState<'owner' | 'admin' | 'member' | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // MSP context-aware profile data - based on current org's domain
  const [contextVendorProfile, setContextVendorProfile] = useState<VendorProfile | null>(null);
  const [contextClaimableProfiles, setContextClaimableProfiles] = useState<VendorProfile[]>([]);
  const [contextDomain, setContextDomain] = useState<string | null>(null);
  const [contextTeamMembers, setContextTeamMembers] = useState<VendorTeamMember[]>([]);
  const [contextUserRole, setContextUserRole] = useState<'owner' | 'admin' | 'member' | null>(null);
  const [contextLoading, setContextLoading] = useState(true);
  const [contextVendorError, setContextVendorError] = useState<string | null>(null);
  const [pendingInvitations, setPendingInvitations] = useState<PendingVendorInvitation[]>([]);
  const [pendingInvitationsError, setPendingInvitationsError] = useState(false);
  // Request generation: a load whose user changed before it resolved is discarded,
  // so one user's invitations never repopulate under another (or a cleared) user.
  const pendingInvitationsGenerationRef = useRef(0);
  // The user id the currently-committed pendingInvitations belong to, so a user
  // switch can be detected and cleared IMMEDIATELY — even while auth is still
  // loading — instead of leaving A's invitations visible to B during the switch.
  const pendingInvitationsUserRef = useRef<string | null>(null);
  const [completedClaimWorkspace, setCompletedClaimWorkspace] = useState<CompletedClaimWorkspace | null>(null);
  // Organization switches and claim refreshes can overlap. Only the newest
  // workspace lookup may commit provider state or clear the loading gate.
  const contextRequestGenerationRef = useRef(0);
  // Explicit advisory profile switches can overlap for team-only suppliers too.
  const vendorRequestGenerationRef = useRef(0);
  // ENT-3079: settled-workspace keys. A benign focus-return auth re-emit (e.g.
  // supabase-js firing SIGNED_IN for the SAME user when a tab regains
  // visibility) hands consumers new user/organization object identities without
  // changing WHICH workspace is active. Re-running the bootstrap for an
  // unchanged key must revalidate in the background instead of re-entering
  // loading — VendorRouteGuard blanks the whole supplier shell while loading,
  // which reads as a full page refresh on tab return.
  const lastSettledVendorBootstrapKeyRef = useRef<string | null>(null);
  const lastSettledContextBootstrapKeyRef = useRef<string | null>(null);
  const shouldHydrate = shouldHydrateVendorContext(location.pathname);
  const vendorBootstrapDelayMs = getVendorContextBootstrapDelayMs(location.pathname);
  
  // Get domain from URL if present (for claim page)
  const urlDomain = typeof window !== 'undefined'
    ? normalizeDomainInput(new URLSearchParams(window.location.search).get('domain'))
    : null;
  // Advisory and Action deep links may name a supplier team profile. Resolve it
  // through the membership-backed vendor bootstrap before either route queries.
  const requestedAdvisoryProfileId = location.pathname === '/vendor/actions' || location.pathname === '/vendor/advisories' || location.pathname.startsWith('/vendor/advisories/')
    ? new URLSearchParams(location.search).get('profile') : null;

  const fetchVendorData = useCallback(async (showLoadingState: boolean = true) => {
    const requestGeneration = ++vendorRequestGenerationRef.current;
    const isLatestRequest = () => vendorRequestGenerationRef.current === requestGeneration;
    if (!shouldHydrate) {
      lastSettledVendorBootstrapKeyRef.current = null;
      setVendorProfile(null);
      setClaimableProfiles([]);
      setTeamMembers([]);
      setUserRole(null);
      setLoading(false);
      return;
    }

    // Don't set loading=false while auth is still loading - wait for user to be determined
    if (authLoading) {
      return;
    }

    if (!user) {
      lastSettledVendorBootstrapKeyRef.current = null;
      setVendorProfile(null);
      setClaimableProfiles([]);
      setTeamMembers([]);
      setUserRole(null);
      setLoading(false);
      return;
    }

    // ENT-3079: an unchanged settled workspace revalidates without a loading
    // flash; a genuinely different user/domain still shows the loading state.
    // A claimant-derived supplier member has no vendor_team_members row. Its
    // accessible profile is instead bound to one of the user's organisations,
    // which can differ from the active workspace when vendor view was restored
    // after sign-in. Include that complete membership set in the request key so
    // a refreshed organisation bootstrap cannot retain an older profile.
    const activeOrganizationId = organization?.id ?? null;
    const accessibleOrganizationIds = collectAccessibleOrganizationIds([organization, ...organizations, ...mspCustomerOrgs]);
    const vendorBootstrapKey = requestedAdvisoryProfileId
      ? buildRequestedVendorBootstrapKey(user.id, urlDomain, requestedAdvisoryProfileId, accessibleOrganizationIds)
      : `${user.id}:${urlDomain ?? ''}:${activeOrganizationId ?? ''}:${accessibleOrganizationIds.join(',')}`;
    const shouldShowLoadingState =
      showLoadingState && lastSettledVendorBootstrapKeyRef.current !== vendorBootstrapKey;
    if (shouldShowLoadingState) {
      setLoading(true);
    }
    setError(null);

    try {
      const emailDomain = user.email?.split('@')[1]?.toLowerCase();

      if (!emailDomain) {
        setVendorProfile(null);
        setClaimableProfiles([]);
        setTeamMembers([]);
        setUserRole(null);
        return;
      }

      const bootstrap = await vendorBootstrapDeduper.load(
        vendorBootstrapKey,
        async (): Promise<VendorBootstrapState> => {
          const { data: membershipsData, error: membershipsError } = await supabase
            .from('vendor_team_members')
            .select(`
              *,
              vendor_profile:vendor_profiles(*)
            `)
            .eq('user_id', user.id)
            .not('accepted_at', 'is', null);

          if (membershipsError) {
            if (!isNavigationAbortedFetch(membershipsError)) {
              console.error('Error fetching vendor memberships:', membershipsError);
            }
            throw membershipsError;
          }

          const memberships = (membershipsData || []) as VendorMembershipRecord[];
          const membershipListingMap = await loadVendorListingMap(
            memberships
              .map((membership) => membership.vendor_profile?.marketplace_listing_id)
              .filter((listingId): listingId is string => Boolean(listingId)),
          );
          const preferredDomain = urlDomain || emailDomain;
          const preferredMembership = preferredDomain
            ? memberships.find((membership) => {
              const listing = membership.vendor_profile?.marketplace_listing_id
                ? membershipListingMap.get(membership.vendor_profile.marketplace_listing_id)
                : undefined;
              return resolveVendorProfileDomain(listing) === preferredDomain;
            })
            : undefined;

          const membershipData = requestedAdvisoryProfileId
            ? memberships.find((membership) => membership.vendor_profile?.id === requestedAdvisoryProfileId)
            : preferredMembership || memberships[0];

          const physicalProfile = membershipData?.vendor_profile
            ? toVendorProfile(
              membershipData.vendor_profile as unknown as VendorProfileRecord,
              membershipListingMap,
            )
            : null;

          const loadPhysicalProfile = async (requireResolvedRole = false): Promise<VendorBootstrapState | null> => {
            if (!physicalProfile) return null;
            const membershipState = await loadVendorMembershipState(physicalProfile.id, user.id);
            if (requireResolvedRole && !membershipState.userRole) return null;
            return {
              vendorProfile: physicalProfile,
              claimableProfiles: [],
              teamMembers: membershipState.teamMembers,
              userRole: membershipState.userRole,
            };
          };

          const physicalMatchesPreferredDomain = physicalProfile
            && resolveVendorProfileDomain(membershipListingMap.get(physicalProfile.marketplace_listing_id)) === preferredDomain;

          if (physicalMatchesPreferredDomain) {
            const physicalBootstrap = await loadPhysicalProfile(true);
            if (physicalBootstrap) return physicalBootstrap;
          }

          // An accepted vendor-team membership identifies an explicit supplier
          // workspace. Keep it ahead of a claimant-derived fallback even when
          // its domain differs from the login email or deep-link preference.
          const physicalBootstrap = await loadPhysicalProfile();
          if (physicalBootstrap) return physicalBootstrap;

          // ENT-6063: the portal-access preference admits claimant members with
          // no physical supplier-team row. This is route UX only: the score
          // reader independently derives and enforces resource access server-side.
          if (accessibleOrganizationIds.length > 0) {
            // A raw organisation row is not claimant access: the canonical
            // predicate excludes customer-service and expired break-glass rows
            // and admits managed customers. One set-wise server read applies
            // internal_security.is_vendor_team_member to every candidate; the
            // organisation list only narrows candidates. Unreadable fails closed.
            const { data: accessibleProfileIds, error: accessibleProfilesError } = await supabase.rpc(
              'list_accessible_claimant_vendor_profile_ids',
              { p_organization_ids: accessibleOrganizationIds },
            );

            if (accessibleProfilesError) {
              if (!isNavigationAbortedFetch(accessibleProfilesError)) {
                console.error('Error resolving claimant vendor profile access:', accessibleProfilesError);
              }
              throw accessibleProfilesError;
            }

            const verifiedProfileIds = (accessibleProfileIds ?? []) as string[];
            let derivedProfiles: VendorProfileRecord[] = [];
            if (verifiedProfileIds.length > 0) {
              const { data: derivedProfilesData, error: derivedProfilesError } = await supabase
                .from('vendor_profiles')
                .select('*')
                .in('id', verifiedProfileIds);

              if (derivedProfilesError) {
                if (!isNavigationAbortedFetch(derivedProfilesError)) {
                  console.error('Error fetching claimant-derived vendor profiles:', derivedProfilesError);
                }
                throw derivedProfilesError;
              }
              derivedProfiles = (derivedProfilesData ?? []) as VendorProfileRecord[];
            }

            if (derivedProfiles.length > 0) {
              const derivedListingMap = await loadVendorListingMap(
                derivedProfiles.map((profile) => profile.marketplace_listing_id),
              );

              // A claimed profile is tenant-owned. Prefer a profile for the
              // active customer shell, but a stale unrelated shell must not
              // hide the only claimant profile that can open a resource-scoped
              // score report. If no active-tenant profile exists, fall back to
              // all accessible profiles and require the same unique selection
              // rule used without an active shell. Never choose by UUID order.
              // An explicit advisory/action profile selects only that profile.
              const requestedDerivedProfiles = requestedAdvisoryProfileId
                ? derivedProfiles.filter((profile) => profile.id === requestedAdvisoryProfileId)
                : derivedProfiles;
              const activeTenantDerivedProfiles = activeOrganizationId
                ? requestedDerivedProfiles.filter((profile) => (
                  readClaimantOrganizationId(profile) === activeOrganizationId
                ))
                : [];
              const selectableDerivedProfiles = activeTenantDerivedProfiles.length > 0
                ? activeTenantDerivedProfiles
                : requestedDerivedProfiles;
              const preferredDerivedProfiles = preferredDomain
                ? selectableDerivedProfiles.filter((profile) => (
                  resolveVendorProfileDomain(derivedListingMap.get(profile.marketplace_listing_id)) === preferredDomain
                ))
                : [];
              const derivedProfile = preferredDerivedProfiles.length === 1
                ? preferredDerivedProfiles[0]
                : selectableDerivedProfiles.length === 1
                  ? selectableDerivedProfiles[0]
                  : null;

              if (derivedProfile) {
                return {
                  vendorProfile: toVendorProfile(derivedProfile, derivedListingMap),
                  claimableProfiles: [],
                  teamMembers: [],
                  userRole: 'member',
                };
              }
            }
          }

          if (requestedAdvisoryProfileId) {
            return { vendorProfile: null, claimableProfiles: [], teamMembers: [], userRole: null };
          }
          let allClaimableProfiles: VendorProfile[] = [];

          // ENT-6257 (Codex r4419168010): one profile lookup serves both lists. Unclaimed
          // profiles are claimable; a claimed one (for example Supply Guard managed, which is
          // requested, not self-claimed) removes its listing from the synthetic claimables. If the
          // lookup fails, offer no listing-derived claimables rather than ones that may be
          // claimed (fail closed). One call keeps the bootstrap's request count unchanged.
          let claimableProfilesForDomain: VendorProfile[] = [];
          let claimedListingIds: string[] | null = [];
          try {
            const domainProfiles = (await getVendorProfilesForDomain(
              emailDomain,
              ['unclaimed', 'claimed', 'verified'],
            )) as unknown as VendorProfile[];
            const isClaimed = (profile: VendorProfile) => profile.status === 'claimed' || profile.status === 'verified';
            claimableProfilesForDomain = domainProfiles.filter((profile) => !isClaimed(profile));
            claimedListingIds = domainProfiles.filter(isClaimed).map((profile) => profile.marketplace_listing_id);
          } catch (claimableError) {
            claimedListingIds = null;
            // A lookup the browser cancelled because the page is navigating away is not a
            // read failure (ENT-5717); the same failure on a live page is still reported.
            if (!isNavigationAbortedFetch(claimableError)) {
              console.error('Error fetching claimable profiles:', claimableError);
            }
          }

          if (claimableProfilesForDomain.length > 0) {
            const claimableListingMap = await loadVendorListingMap(
              claimableProfilesForDomain.map((profile) => profile.marketplace_listing_id),
            );
            allClaimableProfiles = attachListingsToProfiles(claimableProfilesForDomain, claimableListingMap);
          }

          let listingsData: MarketplaceListingRecord[] = [];
          try {
            listingsData = (await getActiveMarketplaceListingsForDomain(emailDomain, MARKETPLACE_LISTING_SELECT)) as MarketplaceListingRecord[];
          } catch (listingsError) {
            if (!isNavigationAbortedFetch(listingsError)) {
              console.error('Error fetching claimable listings:', listingsError);
            }
          }

          if (listingsData.length > 0 && claimedListingIds !== null) {
            const existingProfileListingIds = allClaimableProfiles.map(p => p.marketplace_listing_id);
            const knownClaimedListingIds = claimedListingIds;
            const newListings = listingsData.filter(
              l => !existingProfileListingIds.includes(l.id) && !knownClaimedListingIds.includes(l.id),
            );

            const claimableFromListings: VendorProfile[] = newListings.map(listing => ({
              id: `pending-${listing.id}`,
              marketplace_listing_id: listing.id,
              hide_marketplace_scores: false,
              domain: normalizeDomainInput(listing.domain) || emailDomain,
              claimed_at: null,
              claimed_by: null,
              status: 'unclaimed' as const,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              listing: {
                company_name: listing.company_name,
                logo_url: listing.display_logo_url,
                logo_source: listing.logo_source ?? null,
                website_url: listing.website_url,
                service_description: listing.service_description,
                supplier_type: listing.supplier_type,
                country: listing.country,
                confirmedCountryCode: listing.confirmed_country_code ?? null,
                status: listing.status ?? null,
                domain: listing.domain ?? null,
              },
            }));
            allClaimableProfiles = [...allClaimableProfiles, ...claimableFromListings];
          }

          return {
            vendorProfile: null,
            claimableProfiles: allClaimableProfiles,
            teamMembers: [],
            userRole: null,
          };
        },
        15_000,
      );

      if (!isLatestRequest()) return;
      setVendorProfile(bootstrap.vendorProfile);
      setClaimableProfiles(bootstrap.claimableProfiles);
      setTeamMembers(bootstrap.teamMembers);
      setUserRole(bootstrap.userRole);
      lastSettledVendorBootstrapKeyRef.current = vendorBootstrapKey;
    } catch (err) {
      if (!isLatestRequest()) return;
      if (!isNavigationAbortedFetch(err)) console.error('Error in fetchVendorData:', err);
      if (!isLatestRequest()) return;
      // A failed bootstrap must not retain a previously committed supplier
      // workspace. In particular, a user or organisation switch can make the
      // next membership lookup fail after the prior profile was rendered.
      // Leaving that data in context would let downstream vendor routes act on
      // the stale profile while exposing it to the new workspace.
      setVendorProfile(null);
      setClaimableProfiles([]);
      setTeamMembers([]);
      setUserRole(null);
      lastSettledVendorBootstrapKeyRef.current = null;
      setError('Failed to load vendor data');
    } finally {
      if (isLatestRequest() && showLoadingState) {
        setLoading(false);
      }
    }
  }, [authLoading, mspCustomerOrgs, organization, organizations, requestedAdvisoryProfileId, shouldHydrate, urlDomain, user]);

  // Fetch vendor profile for the current organization context so customer-shell
  // supplier links resolve against the active workspace rather than any stale
  // personal/vendor membership.
  const fetchContextVendorData = useCallback(async () => {
    const requestGeneration = ++contextRequestGenerationRef.current;
    setContextVendorError(null);
    const isLatestRequest = () => contextRequestGenerationRef.current === requestGeneration;

    if (!shouldHydrate) {
      // ENT-3079 (Codex P2): clearing context state must also clear the settled
      // key. Otherwise visiting a non-hydrating route (e.g. /auth) wipes the
      // context but leaves the key, so returning to /vendor as the same
      // user+org skips the loading gate and VendorRouteGuard renders the
      // "Supplier profile unavailable" state from the cleared context before the
      // background bootstrap restores it.
      lastSettledContextBootstrapKeyRef.current = null;
      setContextVendorProfile(null);
      setContextClaimableProfiles([]);
      setContextDomain(null);
      setContextTeamMembers([]);
      setContextUserRole(null);
      setContextLoading(false);
      return;
    }

    if (!organization) {
      lastSettledContextBootstrapKeyRef.current = null;
      setContextVendorProfile(null);
      setContextClaimableProfiles([]);
      setContextDomain(null);
      setContextTeamMembers([]);
      setContextUserRole(null);
      setContextLoading(false);
      return;
    }

    if (!user) {
      lastSettledContextBootstrapKeyRef.current = null;
      setContextVendorProfile(null);
      setContextClaimableProfiles([]);
      setContextDomain(null);
      setContextTeamMembers([]);
      setContextUserRole(null);
      setContextLoading(false);
      return;
    }

    const orgDomain = readWorkspaceDomain(organization) || organization.slug?.toLowerCase();
    const explicitVendorProfileId = readWorkspaceVendorProfileId(organization);

    // ENT-3079: settled-workspace rule — an unchanged workspace revalidates in
    // the background without blanking the shell through the contextLoading gate.
    // The key MUST include the same supplier-binding inputs the bootstrap request
    // keys on (vendor_profile_id / domain / slug), not just the org id: an org
    // refreshed with the SAME id but a changed binding selects a DIFFERENT
    // supplier, so skipping the loading gate there would leave the previous
    // supplier's contextVendorProfile/contextUserRole rendered while the new
    // bootstrap runs — a cross-supplier fail-open (Codex P2). Keying on the
    // binding makes protected vendor pages fail closed (re-enter loading) when
    // the active supplier actually changes.
    const contextBootstrapKey =
      `${user.id}:${organization.id}:${explicitVendorProfileId ?? orgDomain ?? 'typed-claimant'}`;
    if (lastSettledContextBootstrapKeyRef.current !== contextBootstrapKey) {
      setContextLoading(true);
    }

    try {
      const bootstrap = await contextVendorBootstrapDeduper.load(
        `${organization.id}:${explicitVendorProfileId ?? orgDomain ?? 'typed-claimant'}`,
        async (): Promise<ContextVendorBootstrapState> => {
          const { data: typedClaimedProfile, error: typedClaimedProfileError } = await supabase
            .from('vendor_profiles')
            .select(`*`)
            .eq(CLAIMANT_ORGANIZATION_ID_COLUMN, organization.id)
            .in('status', ['claimed', 'verified'])
            .maybeSingle();

          if (typedClaimedProfileError) throw typedClaimedProfileError;
          if (typedClaimedProfile) {
            const typedProfile = typedClaimedProfile as unknown as VendorProfileRecord;
            const typedListingMap = await loadVendorListingMap([typedProfile.marketplace_listing_id]);
            const typedVendorProfile = toVendorProfile(typedProfile, typedListingMap);
            const membershipState = await loadVendorMembershipState(typedProfile.id, user.id);
            return {
              contextVendorProfile: typedVendorProfile,
              contextClaimableProfiles: [],
              contextDomain: orgDomain ?? normalizeDomainInput(typedVendorProfile.domain),
              contextTeamMembers: membershipState.teamMembers,
              contextUserRole: membershipState.userRole,
            };
          }

          if (!orgDomain) {
            return {
              contextVendorProfile: null,
              contextClaimableProfiles: [],
              contextDomain: null,
              contextTeamMembers: [],
              contextUserRole: null,
            };
          }

          if (explicitVendorProfileId) {
            const { data: explicitClaimedProfile } = await supabase
              .from('vendor_profiles')
              .select(`*`)
              .eq('id', explicitVendorProfileId)
              .in('status', ['claimed', 'verified'])
              .maybeSingle();

            // ENT-6257 (D5): a Supply Guard managed profile is never an organisation's own context,
            // even through a stale settings link; its operators reach it through their team row.
            if (explicitClaimedProfile && (explicitClaimedProfile as { stewardship?: string | null }).stewardship !== 'supply_guard') {
              const explicitProfile = explicitClaimedProfile as unknown as VendorProfileRecord;
              const explicitListingMap = await loadVendorListingMap([explicitProfile.marketplace_listing_id]);
              const explicitVendorProfile = toVendorProfile(explicitProfile, explicitListingMap);
              const membershipState = await loadVendorMembershipState(explicitProfile.id, user.id);
              return {
                contextVendorProfile: explicitVendorProfile,
                contextClaimableProfiles: [],
                contextDomain: orgDomain,
                contextTeamMembers: membershipState.teamMembers,
                contextUserRole: membershipState.userRole,
              };
            }

            const { data: explicitUnclaimedProfile } = await supabase
              .from('vendor_profiles')
              .select(`*`)
              .eq('id', explicitVendorProfileId)
              .eq('status', 'unclaimed')
              .maybeSingle();

            if (explicitUnclaimedProfile) {
              const explicitProfile = explicitUnclaimedProfile as unknown as VendorProfileRecord;
              const explicitListingMap = await loadVendorListingMap([explicitProfile.marketplace_listing_id]);
              return {
                contextVendorProfile: null,
                contextClaimableProfiles: attachListingsToProfiles(
                  [{
                    id: explicitProfile.id,
                    marketplace_listing_id: explicitProfile.marketplace_listing_id,
                    hide_marketplace_scores: explicitProfile.hide_marketplace_scores ?? false,
                    claimed_at: explicitProfile.claimed_at,
                    claimed_by: explicitProfile.claimed_by,
                    status: explicitProfile.status,
                    created_at: explicitProfile.created_at,
                    updated_at: explicitProfile.updated_at,
                  }],
                  explicitListingMap,
                ),
                contextDomain: orgDomain,
                contextTeamMembers: [],
                contextUserRole: null,
              };
            }

            return {
              contextVendorProfile: null,
              contextClaimableProfiles: [],
              contextDomain: null,
              contextTeamMembers: [],
              contextUserRole: null,
            };
          }

          const claimedProfiles = await getVendorProfilesForDomain(orgDomain, ['claimed', 'verified']);
          const claimedProfile = claimedProfiles[0] ?? null;
          const claimedMembershipState = claimedProfile
            ? await loadVendorMembershipState(claimedProfile.id, user.id)
            : null;
          // ENT-6257 (D5): a Supply Guard managed profile is never an organisation's own vendor
          // context by domain match. Only its operators (team members) work in it; everyone else
          // stays on the claim flow, where the managed profile is requested, not opened.
          const claimedProfileIsForeignManaged = claimedProfile?.stewardship === 'supply_guard'
            && !claimedMembershipState?.userRole;

          if (claimedProfile && claimedMembershipState && !claimedProfileIsForeignManaged) {
            const claimedListingMap = await loadVendorListingMap([claimedProfile.marketplace_listing_id]);
            const membershipState = claimedMembershipState;
            return {
              contextVendorProfile: toVendorProfile(claimedProfile as unknown as VendorProfileRecord, claimedListingMap),
              contextClaimableProfiles: [],
              contextDomain: orgDomain,
              contextTeamMembers: membershipState.teamMembers,
              contextUserRole: membershipState.userRole,
            };
          }

          let allClaimable: VendorProfile[] = [];

          const unclaimedProfiles = await getVendorProfilesForDomain(orgDomain, ['unclaimed']);

          if (unclaimedProfiles.length > 0) {
            const claimableProfiles = unclaimedProfiles as unknown as VendorProfile[];
            const claimableListingMap = await loadVendorListingMap(
              claimableProfiles.map((profile) => profile.marketplace_listing_id),
            );
            allClaimable = attachListingsToProfiles(claimableProfiles, claimableListingMap);
          }

          const listingsData = (await getActiveMarketplaceListingsForDomain(
            orgDomain,
            MARKETPLACE_LISTING_SELECT,
          )) as MarketplaceListingRecord[];

          if (listingsData && listingsData.length > 0) {
            const existingProfileListingIds = allClaimable.map(p => p.marketplace_listing_id);
            // A listing with a claimed (e.g. Supply Guard managed) profile is never offered as claimable.
            const claimedListingIds = claimedProfiles.map((profile) => profile.marketplace_listing_id);
            const newListings = listingsData.filter(
              l => !existingProfileListingIds.includes(l.id) && !claimedListingIds.includes(l.id),
            );

            const claimableFromListings: VendorProfile[] = newListings.map(listing => ({
              id: `pending-${listing.id}`,
              marketplace_listing_id: listing.id,
              hide_marketplace_scores: false,
              domain: normalizeDomainInput(listing.domain) || orgDomain,
              claimed_at: null,
              claimed_by: null,
              status: 'unclaimed' as const,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              listing: {
                company_name: listing.company_name,
                logo_url: listing.display_logo_url,
                logo_source: listing.logo_source ?? null,
                website_url: listing.website_url,
                service_description: listing.service_description,
                supplier_type: listing.supplier_type,
                country: listing.country,
                confirmedCountryCode: listing.confirmed_country_code ?? null,
                status: listing.status ?? null,
                domain: listing.domain ?? null,
              },
            }));
            allClaimable = [...allClaimable, ...claimableFromListings];
          }

          return {
            contextVendorProfile: null,
            contextClaimableProfiles: allClaimable,
            contextDomain: orgDomain,
            contextTeamMembers: [],
            contextUserRole: null,
          };
        },
        15_000,
      );

      if (!isLatestRequest()) {
        return;
      }

      setContextDomain(bootstrap.contextDomain);
      setContextVendorProfile(bootstrap.contextVendorProfile);
      setContextClaimableProfiles(bootstrap.contextClaimableProfiles);
      setContextTeamMembers(bootstrap.contextTeamMembers);
      setContextUserRole(bootstrap.contextUserRole);
      lastSettledContextBootstrapKeyRef.current = contextBootstrapKey;
    } catch (err) {
      if (!isNavigationAbortedFetch(err)) console.error('Error in fetchContextVendorData:', err);
      if (isLatestRequest()) {
        setContextVendorError('Could not load the company supplier profile. Try again.');
        lastSettledContextBootstrapKeyRef.current = null;
        setContextVendorProfile(null);
        setContextClaimableProfiles([]);
        setContextDomain(null);
        setContextTeamMembers([]);
        setContextUserRole(null);
      }
    } finally {
      if (isLatestRequest()) {
        setContextLoading(false);
      }
    }
  }, [organization, shouldHydrate, user]);

  const claimProfile = async (
    profileId: string,
    overrideDomain?: string
  ): Promise<{ error: string | null; claimedProfileId?: string }> => {
    if (!user) {
      return { error: 'Not authenticated' };
    }

    try {
      const emailDomain = normalizeDomainInput(user.email?.split('@')[1]);
      // Use override domain for MSP claiming, otherwise use email domain
      const claimingDomain = normalizeDomainInput(overrideDomain) || emailDomain;
      
      if (!claimingDomain) {
        return { error: 'Invalid domain for claiming' };
      }

      let actualProfileId = profileId;
      let vendorProfileId: string | null = null;
      let marketplaceListingId: string | null = null;
      let supplierWorkspaceName: string | null = null;

      // Check if this is a pending profile (created from marketplace listing)
      if (profileId.startsWith('pending-')) {
        const listingId = profileId.replace('pending-', '');
        marketplaceListingId = listingId;

        const pendingProfile = [...claimableProfiles, ...contextClaimableProfiles].find(
          (profile) => profile.id === profileId || profile.marketplace_listing_id === listingId,
        );
        supplierWorkspaceName =
          typeof pendingProfile?.listing?.company_name === 'string'
            ? pendingProfile.listing.company_name
            : null;

        const pendingProfileDomain = normalizeDomainInput(pendingProfile?.domain ?? null);
        const isAllowedDomainForListing = pendingProfileDomain === claimingDomain
          || (await isSupplierDomainAllowedForListing(listingId, claimingDomain));

        // Only enforce domain match for non-MSP claiming (when no override domain)
        if (!overrideDomain && !isAllowedDomainForListing) {
          return { error: `You can only claim profiles matching your email domain (@${emailDomain})` };
        }
      } else {
        vendorProfileId = profileId;

        // Verify the existing profile's linked listing allows the claiming domain.
        const { data: existingProfile, error: profileError } = await supabase
          .from('vendor_profiles')
          .select(`status, marketplace_listing_id, claimed_by, ${CLAIMANT_ORGANIZATION_ID_COLUMN}`)
          .eq('id', profileId)
          .single();

        if (profileError || !existingProfile) {
          return { error: 'Profile not found' };
        }

        const isAllowedDomainForListing = await isSupplierDomainAllowedForListing(
          existingProfile.marketplace_listing_id,
          claimingDomain,
        );

        // Only enforce domain match for non-MSP claiming (when no override domain)
        if (!overrideDomain && !isAllowedDomainForListing) {
          return { error: `You can only claim profiles matching your email domain (@${emailDomain})` };
        }

        if (existingProfile.status === 'claimed') {
          const canRepairOwnerlessOrgLinkedClaim =
            !existingProfile.claimed_by && Boolean(readClaimantOrganizationId(existingProfile));
          if (existingProfile.claimed_by !== user.id && !canRepairOwnerlessOrgLinkedClaim) {
            return { error: 'This profile has already been claimed' };
          }
        }
      }

      const { data: claimResult, error: claimError } = await supabase.rpc('claim_vendor_profile_with_workspace', {
        p_vendor_profile_id: vendorProfileId,
        p_marketplace_listing_id: marketplaceListingId,
        p_claiming_domain: claimingDomain,
        p_existing_organization_id: organization?.id ?? null,
        p_workspace_name: supplierWorkspaceName,
      });

      if (claimError) {
        console.error('Error claiming vendor profile:', claimError);
        return { error: mapVendorClaimRpcError(claimError.message) };
      }

      const claimPayload = claimResult as {
        vendor_profile_id?: string | null;
        organization_id?: string | null;
        organization_created?: boolean | null;
      } | null;
      actualProfileId = claimPayload?.vendor_profile_id ?? actualProfileId;

      if (claimPayload?.organization_id && claimPayload.organization_id !== organization?.id) {
        try {
          await refetchOrganizations();
        } catch (refetchError) {
          console.error('Error refreshing organisations after supplier workspace claim:', refetchError);
        }
      }

      // Refresh data
      vendorBootstrapDeduper.clear();
      contextVendorBootstrapDeduper.clear();
      await fetchVendorData();
      if (claimPayload?.organization_id && actualProfileId) {
        setCompletedClaimWorkspace({
          organizationId: claimPayload.organization_id,
          vendorProfileId: actualProfileId,
        });
      }
      return { error: null, claimedProfileId: actualProfileId };
    } catch (err) {
      console.error('Error in claimProfile:', err);
      return { error: 'An unexpected error occurred' };
    }
  };

  useEffect(() => {
    if (!shouldHydrate) {
      setLoading(false);
      return;
    }

    if (vendorBootstrapDelayMs <= 0) {
      void fetchVendorData();
      return undefined;
    }

    setLoading(false);
    const timeoutId = window.setTimeout(() => {
      void fetchVendorData(false);
    }, vendorBootstrapDelayMs);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [fetchVendorData, shouldHydrate, vendorBootstrapDelayMs]);

  // Fetch context-aware vendor data when organization changes (especially for MSP context)
  useEffect(() => {
    if (!shouldHydrate) {
      return;
    }

    void fetchContextVendorData();
  }, [fetchContextVendorData, shouldHydrate]);

  const completedClaimContextProfile = completedClaimWorkspace
    && vendorProfile
    && completedClaimWorkspace.organizationId === organization?.id
    && completedClaimWorkspace.vendorProfileId === vendorProfile.id
    ? withClaimantOrganizationId(vendorProfile, completedClaimWorkspace.organizationId)
    : null;
  const resolvedContextVendorProfile = contextVendorProfile ?? completedClaimContextProfile;
  const usingCompletedClaimContext = !contextVendorProfile && Boolean(completedClaimContextProfile);
  const hasResolvedContextSupplierProfile = Boolean(resolvedContextVendorProfile)
    || Boolean(contextClaimableProfiles.length);
  const requestedBaseProfile = Boolean(requestedAdvisoryProfileId && vendorProfile?.id === requestedAdvisoryProfileId);
  const requestedContextProfile = Boolean(requestedAdvisoryProfileId && resolvedContextVendorProfile?.id === requestedAdvisoryProfileId);
  // ENT-6355: an operator's `?domain=` deep link to a Supply Guard managed profile
  // they maintain names that profile explicitly; the active organisation's own
  // supplier profile must not replace it. The base profile only comes from the
  // operator's accepted team membership, and every reader re-authorizes it.
  const requestedManagedBaseProfile = Boolean(
    urlDomain
      && vendorProfile?.stewardship === 'supply_guard'
      && vendorProfile.domain === urlDomain
      && resolvedContextVendorProfile?.id !== vendorProfile.id,
  );
  const shouldPreferContextProfile = requestedAdvisoryProfileId
    ? !requestedBaseProfile && requestedContextProfile
    : Boolean(organization) && hasResolvedContextSupplierProfile && !requestedManagedBaseProfile;

  // Prefer the workspace-linked supplier profile whenever the current org can
  // be resolved to one. That keeps customer-shell switching, breakglass, and
  // MSP impersonation aligned to the active workspace.
  const effectiveVendorProfile = requestedAdvisoryProfileId && !requestedBaseProfile && !requestedContextProfile
    ? null : shouldPreferContextProfile ? resolvedContextVendorProfile : vendorProfile;
  const effectiveClaimableProfiles = requestedAdvisoryProfileId ? [] : shouldPreferContextProfile ? contextClaimableProfiles : claimableProfiles;
  const effectiveTeamMembers = !effectiveVendorProfile ? [] : shouldPreferContextProfile && !usingCompletedClaimContext
    ? contextTeamMembers
    : teamMembers;
  const effectiveUserRole = !effectiveVendorProfile ? null : shouldPreferContextProfile && !usingCompletedClaimContext
    ? contextUserRole
    : userRole;
  const canManageProfile = effectiveUserRole === 'owner' || effectiveUserRole === 'admin';
  const isVendorMode = !!effectiveVendorProfile;
  const requestedProfileError = error ?? (requestedAdvisoryProfileId && !requestedBaseProfile ? contextVendorError : null);
  const expectedContextKey = user && organization
    ? `${user.id}:${organization.id}:${readWorkspaceVendorProfileId(organization) ?? (readWorkspaceDomain(organization) || organization.slug?.toLowerCase()) ?? 'typed-claimant'}` : null;
  const requestedProfilePending = Boolean(user?.id && requestedAdvisoryProfileId && !requestedProfileError && !requestedContextProfile
    && (lastSettledVendorBootstrapKeyRef.current !== buildRequestedVendorBootstrapKey(
      user.id,
      urlDomain,
      requestedAdvisoryProfileId,
      collectAccessibleOrganizationIds([organization, ...organizations, ...mspCustomerOrgs]),
    )
      || (!requestedBaseProfile && expectedContextKey && lastSettledContextBootstrapKeyRef.current !== expectedContextKey)));
  const fetchPendingInvitations = useCallback(async () => {
    const currentUserId = user?.id ?? null;
    // Codex round 10/11: the active user changed (incl. sign-out). Invalidate any
    // in-flight load and clear the previous user's invitations IMMEDIATELY — BEFORE
    // any early-return guard (the shouldHydrate guard below AND the authLoading
    // guard), so a stale user's late response can never repopulate state for a
    // different session. Round 11: this must precede shouldHydrate too — entering a
    // non-hydrated route (e.g. /auth) returned here without bumping the generation,
    // leaving A's outstanding request able to pass the generation check into B's
    // session.
    if (pendingInvitationsUserRef.current !== currentUserId) {
      pendingInvitationsUserRef.current = currentUserId;
      pendingInvitationsGenerationRef.current += 1;
      setPendingInvitations([]);
      setPendingInvitationsError(false);
    }
    if (!shouldHydrate) {
      return;
    }
    if (authLoading || !user) {
      return;
    }
    const generation = ++pendingInvitationsGenerationRef.current;
    const { invitations, error } = await fetchPendingVendorInvitations(user.id);
    // Discard a superseded load (the active user changed while this was in flight).
    if (pendingInvitationsGenerationRef.current !== generation) {
      return;
    }
    setPendingInvitations(invitations);
    setPendingInvitationsError(error);
  }, [authLoading, shouldHydrate, user]);

  const acceptInvitation = useCallback(
    async (vendorProfileId: string, accessToken?: string): Promise<{ error: string | null }> => {
      const result = await acceptVendorTeamInvitation(vendorProfileId, accessToken);
      if (result.error) {
        return result;
      }
      // Acceptance flips accepted_at, so the active-membership reads now include
      // this profile — clear the bootstrap caches and refresh all vendor state.
      vendorBootstrapDeduper.clear();
      contextVendorBootstrapDeduper.clear();
      await Promise.all([
        fetchVendorData(),
        fetchContextVendorData(),
        fetchPendingInvitations(),
      ]);
      return { error: null };
    },
    [fetchContextVendorData, fetchPendingInvitations, fetchVendorData],
  );

  const refreshVendorData = useCallback(async () => {
    vendorBootstrapDeduper.clear();
    contextVendorBootstrapDeduper.clear();
    await fetchVendorData();
    await fetchContextVendorData();
    await fetchPendingInvitations();
  }, [fetchContextVendorData, fetchPendingInvitations, fetchVendorData]);

  // ENT-3797: surface the current user's pending (unaccepted) invitations so the
  // acceptance path can render them, independent of the active-membership reads.
  // Declared after fetchPendingInvitations so the effect's dependency is
  // initialised before it evaluates (no temporal-dead-zone on first render).
  // Honours the same route-critical deferral as the vendor bootstrap so
  // pending-invitation loading never adds work inside a deferred window (e.g.
  // /marketplace), where invitations are not route-critical.
  useEffect(() => {
    if (!shouldHydrate) {
      setPendingInvitations([]);
      return undefined;
    }
    if (vendorBootstrapDelayMs <= 0) {
      void fetchPendingInvitations();
      return undefined;
    }
    const timeoutId = window.setTimeout(() => {
      void fetchPendingInvitations();
    }, vendorBootstrapDelayMs);
    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [fetchPendingInvitations, shouldHydrate, vendorBootstrapDelayMs]);

  return (
    <VendorContext.Provider
      value={{
        vendorProfile: effectiveVendorProfile,
        requestedAdvisoryProfileId,
        claimableProfiles: effectiveClaimableProfiles,
        teamMembers: effectiveTeamMembers,
        userRole: effectiveUserRole,
        loading: loading || requestedProfilePending,
        error: requestedProfileError,
        claimProfile,
        refreshVendorData,
        refetch: refreshVendorData,
        canManageProfile,
        isVendorMode,
        pendingInvitations,
        pendingInvitationsError,
        refreshPendingInvitations: fetchPendingInvitations,
        acceptInvitation,
        // MSP context-aware values (still exposed for explicit access if needed)
        contextVendorProfile: resolvedContextVendorProfile,
        contextClaimableProfiles,
        contextDomain,
        contextLoading,
      }}
    >
      {children}
    </VendorContext.Provider>
  );
}

export function useVendor() {
  const context = useContext(VendorContext);
  if (context === undefined) {
    throw new Error('useVendor must be used within a VendorProvider');
  }
  return context;
}
